import React, { useState, useEffect } from "react";
import { usePopupState, bindPopper } from "material-ui-popup-state/hooks";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Popper, Grid, Typography, Icon, Box } from "@material-ui/core";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import PropTypes from "prop-types";
import clsx from "clsx";
import { DateTime } from "luxon";
import LuxonUtils from "@date-io/luxon";
import colors from "constants/colors";

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    backgroundColor: colors.white,
    border: `none`,
    zIndex: 600,
    marginTop: "0.75rem",
  },
  popper: {
    // Needs to be this value to appear in front of the header (1100)
    // and behind dialogs (1300)
    zIndex: 1150,
    marginTop: theme.spacing(3),
  },
  datePicker: {
    zIndex: 600,
  },
  frame: {
    border: `1px solid ${theme.palette.grey[400]}`,
    height: "3.5rem",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    transition: "border-color 0.3s",
    pointerEvents: "none",
    "&:hover": {
      borderColor: colors.grey.medium,
      backgroundColor: colors.grey.light,
    },
  },
  active: {
    borderColor: theme.palette.primary.main,
  },
  text: {
    margin: theme.spacing(0, 1),
    color: "rgba(0, 0, 0, 0.5)",
    pointerEvents: "auto",
    cursor: "pointer",
    position: "relative",
    display: "inline-block",
    "&:hover": {
      color: theme.palette.primary.main,
      "&::after": {
        content: '""',
        position: "absolute",
        height: "0.05rem",
        backgroundColor: theme.palette.primary.main,
        left: 0,
        right: 0,
        bottom: "-0.1rem",
      },
    },
    whiteSpace: "nowrap",
    justifyContent: "space-between",
  },
  dash: {
    margin: theme.spacing(0, 1),
    color: theme.palette.text.secondary,
  },
  icon: {
    marginLeft: theme.spacing(1),
    color: colors.grey.main,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  clickableIcon: {
    cursor: "pointer",
    pointerEvents: "auto",
  },
  nonClickableIcon: {
    cursor: "default",
    pointerEvents: "none",
  },

  allDatesAfterButton: {
    marginTop: theme.spacing(1),
    textTransform: "none",
    color: theme.palette.text.primary,

    "&:hover": {
      // backgroundColor: 'transparent',
      backgroundColor: theme.palette.primary.main,
      color: colors.white,
    },
    "&:active": {
      boxShadow: "none",
      backgroundColor: "transparent",
    },
    "&:focus": {
      outline: "none",
    },
  },
}));

/**
 * A table filter component for filtering the value of a column
 *
 * @param {object} props - object containing props for this component
 * @param {string} props.fieldKey - key of the item property that contain this filter value [required]
 * @param {string} props.tableId - id of the table component
 * @param {string} props.label - text to display in the filter input label if no Start Date of End Date for some reason are not displayed
 * @param {object} props.range - numeric filter range object with the min and max range values (example: { min: , max: 50 })
 * @param {object} props.value - numeric filter object with the min and max values (example: { min: 0, max: 50 })
 * @param {Function} props.onFilterChange - function called when user types on a filter input (params: { @see props.fieldKey }, { filter value })
 *
 * @returns {React.Component} - The table filter component
 */
const AmoTableFilterDate = (props) => {
  const { fieldKey, tableId, label, range, value, onFilterChange } = props;

  const baseId = `${tableId ?? "amo-table"}-filter-numeric-${fieldKey}`;

  const classes = useStyles();
  const [internalValue, setInternalValue] = useState(value);
  const [selectedDate, setSelectedDate] = useState("start");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const popupState = usePopupState({
    variant: "popper",
    popupId: `${selectedDate}-popper`,
  });

  useEffect(() => {
    setInternalValue(value);
    const filterKeys = Object.keys(value ?? {}).map((key) => key.toLowerCase());

    if ((startDate || endDate) && filterKeys.length === 0) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [value]);

  const hasSelectedDates = internalValue.min || internalValue.max;

  const labelKeyboardDatePicker = () => {
    if (selectedDate === "start") {
      return "Start date";
    }
    if (selectedDate === "end") {
      return "End date";
    }
    return label;
  };

  const formatDate = (date) => {
    if (!date) return "YYYY/MM/DD";

    let dateTime;
    if (date instanceof Date) {
      dateTime = DateTime.fromJSDate(date);
    } else {
      dateTime = DateTime.fromISO(date);
    }
    return dateTime.isValid ? dateTime.toFormat("yyyy/MM/dd") : "Invalid Date";
  };

  const datePickerValue = () => {
    if (selectedDate === "start") {
      return startDate || range.min || DateTime.now().toJSDate();
    }
    if (selectedDate === "end") {
      return endDate || range.max || DateTime.now().toJSDate();
    }
    return DateTime.now().toJSDate();
  };

  const handleFilterChange = (newValue) => {
    let valueToSet = DateTime.fromISO(newValue);
    if (selectedDate === "start") {
      valueToSet = newValue < range.min ? range.min : newValue;
      setStartDate(valueToSet);
      onFilterChange({ min: valueToSet, max: endDate });
    }
    if (selectedDate === "end") {
      valueToSet = newValue > range.max ? range.max : newValue;
      setEndDate(valueToSet);
      onFilterChange({ min: startDate, max: valueToSet });
    }
  };

  const clearSelectedDates = () => {
    setStartDate(null);
    setEndDate(null);
    onFilterChange({ min: null, max: null });
  };

  const handleTypographyClick = (valueTypography, type) => {
    setSelectedDate(type);
    popupState.toggle(valueTypography);
  };

  const showAllDates = () => {
    let showButton = false;
    let buttonText = "";
    const minDateTime = DateTime.fromJSDate(range.min);
    const maxDateTime = DateTime.fromJSDate(range.max);
    const isOneDayRange = maxDateTime.diff(minDateTime, "days").days === 0;
    if (!endDate && selectedDate === "start" && !isOneDayRange) {
      showButton = true;
      buttonText = "All Dates After >";
    }
    if (!startDate && selectedDate === "end" && !isOneDayRange) {
      showButton = true;
      buttonText = "< All Dates Before";
    }
    return { showButton, buttonText };
  };

  const handleAllDatesAfterClick = () => {
    if (selectedDate === "start") {
      setEndDate(range.max);
      onFilterChange({ min: startDate, max: range.max });
    }
    if (selectedDate === "end") {
      setStartDate(range.min);
      onFilterChange({ min: range.min, max: endDate });
    }
  };

  return (
    <>
      <Box className={clsx(classes.frame, popupState.isOpen && classes.active)}>
        <Typography
          variant="body1"
          className={classes.text}
          onClick={(data) => {
            handleTypographyClick(data, "start");
          }}
        >
          {startDate ? formatDate(startDate) : "YYYY/MM/DD"}
        </Typography>
        <Typography variant="body1" className={classes.dash}>
          -
        </Typography>
        <Typography
          variant="body1"
          className={classes.text}
          onClick={(data) => {
            handleTypographyClick(data, "end");
          }}
        >
          {endDate ? formatDate(endDate) : "YYYY/MM/DD"}
        </Typography>
        <Icon
          className={clsx(
            "material-icons-outlined",
            classes.icon,
            hasSelectedDates ? classes.clickableIcon : classes.nonClickableIcon
          )}
          onClick={hasSelectedDates ? clearSelectedDates : undefined}
        >
          {hasSelectedDates ? "close" : "calendar_today"}
        </Icon>
      </Box>
      <Popper
        id={`id-popper-${selectedDate}`}
        placement="bottom-start"
        className={classes.popper}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...bindPopper(popupState)}
      >
        <Grid
          container
          direction="column"
          spacing={0}
          className={classes.rootContainer}
        >
          <MuiPickersUtilsProvider utils={LuxonUtils}>
            <KeyboardDatePicker
              id={`id-datePicker-${baseId}-${selectedDate}`}
              onChange={(data) => {
                handleFilterChange(data);
              }}
              value={datePickerValue()}
              label={labelKeyboardDatePicker()}
              onClose={() => {
                popupState.toggle();
              }}
              autoOk={false}
              variant="inline"
              color="primary"
              FormHelperTextProps={{ component: "div" }}
              format="yyyy/MM/dd"
              inputVariant="outlined"
              className={classes.datePicker}
              minDate={range.min}
              maxDate={range.max}
            />
            {showAllDates().showButton && (
              <Button
                className={classes.allDatesAfterButton}
                onClick={handleAllDatesAfterClick}
              >
                {showAllDates().buttonText}
              </Button>
            )}
          </MuiPickersUtilsProvider>
        </Grid>
      </Popper>
    </>
  );
};

// set the prop-types for this component
AmoTableFilterDate.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  tableId: PropTypes.string,
  label: PropTypes.string,
  range: PropTypes.exact({
    min: PropTypes.instanceOf(DateTime),
    max: PropTypes.instanceOf(DateTime),
  }),
  value: PropTypes.exact({
    min: PropTypes.instanceOf(DateTime),
    max: PropTypes.instanceOf(DateTime),
  }),
  onFilterChange: PropTypes.func,
};

AmoTableFilterDate.defaultProps = {
  tableId: undefined,
  label: undefined,
  range: {
    min: DateTime.now(),
    max: DateTime.now(),
  },
  value: {},
  onFilterChange: () => {},
};

export default AmoTableFilterDate;
